import classNames from "classnames";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { RelinkUndoPayload } from "../state/graphLegacy";
import {
  projectContents,
  copySceneToProject,
  delinkScene,
  setStartScene,
  undoRelink,
  selectAllGraphContents,
  selectAllGraphContentTypes,
  selectAllGraphActions,
  selectAllGraphPlds,
} from "../state/graphLegacy";
import { isAssetContentType } from "../util/dataTypes/ContentType";
import SearchInput from "../../../common/components/SearchInput/SearchInput";
import type { GraphIssueSeverity } from "../util/graphValidator";
import { isNodeEditable, validateNode } from "../util/graphValidator";
import { collectGraphIssues, groupPldsByContentId } from "../util/collectGraphIssues";
import type { Node, NodeLink, NodeScene, RootLayoutRect } from "../util/nodeTypes";
import useElementRect from "../../../common/util/useElementRect";
import { useNameFormatter, formatContentTypeName } from "../util/NameFormatter";
import Line from "./svg/Line";
import SVGNodeAsset from "./svg/SVGNodeAsset";
import SVGNodeScene from "./svg/SVGNodeScene";
import { detectTypeForAssetNode } from "../util/detectAssetType";
import {
  getIsSilentRefetching,
  getIssuesPanelOpen,
  requestGraphFullReload,
  setGraphIssuesSummary,
  setIssuesPanelOpen,
  setIsValid,
  setShouldRerender,
} from "../state/graphuSlice";
import Spinner from "../../../common/components/Loader/Spinner";
import { getGraphView, setGraphView } from "../state/graphViewSlice";
import "./SVGGraph.scss";
import { useNavigate } from "react-router-dom";
import Icon from "../../../common/components/Icon/Icon";
import CopySceneToProjectModal from "./CopySceneToProjectModal";
import { prettyLabel } from "./DynamicField";
import RelinkSceneModal from "./RelinkSceneModal";
import ModalConfirmation from "../../../common/components/Modal/ModalConfirmation";
import {
  openConfirmationModal,
  closeConfirmationModal,
} from "../../../common/state/slice/modal/modalSlice";
import { setAlert } from "../../alert/state/alertsSlice";
import { createNewScene, deleteNode, getCurrentProject } from "../../projects/state/projectsSlice";
import type { RootState } from "../../../common/state/store";
import { loadingSelector, LoadingState } from "../../../common/state/slice/appStateSlice";
// import { Tooltip as MuiTooltip } from "@mui/material";

const humanizeNodeName = (string: string) => {
  const prefixStart = string.indexOf("_");
  if (prefixStart > -1) {
    string = string.slice(prefixStart + 1);
  }

  // 2) then we split words and keep at most 2 of them
  let elements = string.split(/\s+/g).slice(0, 2);
  elements = elements.flatMap((element) => element.split(/(?=[A-Z]+)/g)).slice(0, 2);

  // 3) optionally we detect "sequenced" strings
  if (elements.length === 1 && /-\d+$/.test(elements[0])) {
    const [firstElement] = elements;
    const suffixStart = firstElement.lastIndexOf("-");
    elements = [firstElement.slice(0, suffixStart), `(${firstElement.slice(suffixStart + 1)})`];
  }

  return elements.join(" ");
};

// Diacritics/case-insensitive haystack for the graph search.
const normalizeForSearch = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Same key as the rendered node elements: render-only clones of an asset
// shared by >1 scene (duplicatedForSceneId) get their own key.
const nodeUniqueKey = (node: any): string =>
  node.type === "asset" && node.duplicatedForSceneId
    ? `${node.modelName}-${node.contentId}-dup${node.duplicatedForSceneId}`
    : `${node.modelName}-${node.contentId}`;

type Props = {
  // nodes to display
  nodes: Node[];
  layout: RootLayoutRect;
  links: NodeLink[];
  // react to selection
  selectedContentId: number | null;
  onSelect?: (node: Node) => void;
  // nodes that arent linked by action
  hiddenSceneNodes?: Node[];
};

const SVGGraph: React.FC<Props> = ({
  nodes,
  layout,
  links,
  selectedContentId,
  hiddenSceneNodes,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentProject = useAppSelector(getCurrentProject);
  const currentProjectGroupId = useAppSelector(
    (state: RootState) => state.projectGroups.current?.id ?? null,
  );
  const isSilentRefetching = useAppSelector(getIsSilentRefetching);

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySceneInfo, setCopySceneInfo] = useState<{
    contentSceneId: number;
    contentId: number;
  } | null>(null);

  const handleCopyScene = (info: { contentSceneId: number; contentId: number }) => {
    setCopySceneInfo(info);
    setCopyModalOpen(true);
  };

  // "Duplicate in this module": same copy endpoint as the cross-module modal,
  // but targeting the current project directly — no module picker detour.
  // While the call is in flight the graph shows the same overlay spinner as
  // the silent refetch, so the click visibly "does something".
  const DUPLICATE_SCENE_COMPONENT = "duplicateSceneInModule";
  const isDuplicatingScene = useAppSelector(
    (s: RootState) => loadingSelector(s, DUPLICATE_SCENE_COMPONENT) === LoadingState.Loading,
  );
  const handleDuplicateScene = async (info: { contentSceneId: number; contentId: number }) => {
    if (!currentProject) return;
    try {
      await dispatch(
        copySceneToProject({
          sourceContentSceneId: info.contentSceneId,
          sourceProjectId: currentProject.id,
          targetProjectId: currentProject.id,
          componentId: DUPLICATE_SCENE_COMPONENT,
        }),
      ).unwrap();
      dispatch(setAlert({ isOpen: true, msg: t("alerts.sceneCopySuccess"), type: "success" }));
      dispatch(setShouldRerender());
    } catch {
      dispatch(setAlert({ isOpen: true, msg: t("alerts.sceneCopyFail"), type: "danger" }));
    }
  };

  const [relinkModalOpen, setRelinkModalOpen] = useState(false);
  const [relinkSceneInfo, setRelinkSceneInfo] = useState<{
    contentSceneId: number;
    contentId: number;
    name?: string;
  } | null>(null);
  // Set only by the "create next scene" flow: pre-selects the relink source.
  const [relinkInitialSourceSceneId, setRelinkInitialSourceSceneId] = useState<number | null>(null);

  const handleRelinkScene = (info: {
    contentSceneId: number;
    contentId: number;
    name?: string;
  }) => {
    setRelinkInitialSourceSceneId(null);
    setRelinkSceneInfo(info);
    setRelinkModalOpen(true);
  };

  // "Create next scene": create a fresh scene (default media) then chain into
  // the relink modal with the source pre-selected — one flow instead of
  // sidebar-create + unlinked strip + relink.
  const CREATE_LINKED_SCENE_COMPONENT = "createLinkedScene";
  const DEFAULT_SCENE_MEDIA_ID = 38;
  const handleCreateLinkedScene = async (info: {
    contentSceneId: number;
    contentId: number;
    name?: string;
  }) => {
    if (!currentProject) return;
    try {
      const res = await dispatch(
        createNewScene({
          componentId: CREATE_LINKED_SCENE_COMPONENT,
          projectId: currentProject.id,
          mediaId: DEFAULT_SCENE_MEDIA_ID,
          model3d: -1,
          sceneName: t("general.defaultSceneName"),
        }),
      ).unwrap();
      const created: any = res.data;
      const newContentId = Number(created?.sceneContent?.id);
      const newContentSceneId = Number(created?.contentScene?.id);
      if (!newContentId || !newContentSceneId) throw new Error("unexpected createScene response");
      // Silent refetch so the new scene lands in the store (relink preview).
      dispatch(setShouldRerender());
      setRelinkInitialSourceSceneId(info.contentSceneId);
      setRelinkSceneInfo({
        contentSceneId: newContentSceneId,
        contentId: newContentId,
        name: created?.contentScene?.name,
      });
      setRelinkModalOpen(true);
    } catch {
      dispatch(setAlert({ isOpen: true, msg: t("alerts.sceneCreateFail"), type: "danger" }));
    }
  };

  // Scene deletion from the canvas "+" menu — same endpoint and confirmation
  // wording as the NodeForm delete button, minus the navigation detour.
  const DELETE_SCENE_COMPONENT = "deleteSceneFromGraph";
  const DELETE_SCENE_MODAL_ID = "delete-scene-from-graph-confirm";
  const [deleteSceneInfo, setDeleteSceneInfo] = useState<{
    contentSceneId: number;
    contentId: number;
    name?: string;
  } | null>(null);

  const handleDeleteScene = (info: {
    contentSceneId: number;
    contentId: number;
    name?: string;
  }) => {
    setDeleteSceneInfo(info);
    dispatch(openConfirmationModal(DELETE_SCENE_MODAL_ID));
  };

  const confirmDeleteScene = async () => {
    if (!deleteSceneInfo) return;
    try {
      await dispatch(
        deleteNode({
          contentId: deleteSceneInfo.contentId,
          componentId: DELETE_SCENE_COMPONENT,
        }),
      ).unwrap();
      dispatch(
        setAlert({
          isOpen: true,
          msg: t("pages.project.edit.deleteSceneSuccess"),
          type: "success",
        }),
      );
      dispatch(closeConfirmationModal(DELETE_SCENE_MODAL_ID));
      setDeleteSceneInfo(null);
      dispatch(setShouldRerender());
    } catch {
      dispatch(
        setAlert({ isOpen: true, msg: t("pages.project.edit.deleteSceneError"), type: "danger" }),
      );
    }
  };

  const DELINK_COMPONENT = "delinkScene";
  const DELINK_MODAL_ID = "delink-scene-confirm";
  const [delinkSceneInfo, setDelinkSceneInfo] = useState<{
    contentSceneId: number;
    contentId: number;
    name?: string;
  } | null>(null);

  const handleDelinkScene = (info: {
    contentSceneId: number;
    contentId: number;
    name?: string;
  }) => {
    setDelinkSceneInfo(info);
    dispatch(openConfirmationModal(DELINK_MODAL_ID));
  };

  const handleSetStartScene = async (info: {
    contentSceneId: number;
    contentId: number;
    name?: string;
  }) => {
    try {
      await dispatch(
        setStartScene({ contentSceneId: info.contentSceneId, componentId: "setStartScene" }),
      ).unwrap();
      dispatch(setShouldRerender());
    } catch {
      dispatch(
        setAlert({ isOpen: true, msg: t("pages.project.edit.relinkError"), type: "danger" }),
      );
    }
  };

  // Undo payload of the LAST relink/delink. One-shot and last-wins: any new
  // link mutation replaces it, using it clears it.
  const [lastUndo, setLastUndo] = useState<RelinkUndoPayload | null>(null);
  const UNDO_COMPONENT = "relinkUndo";

  const confirmDelinkScene = async () => {
    if (!delinkSceneInfo || !currentProject) return;
    try {
      const res = await dispatch(
        delinkScene({
          projectId: currentProject.id,
          contentId: delinkSceneInfo.contentId,
          componentId: DELINK_COMPONENT,
        }),
      ).unwrap();
      setLastUndo((res.data?.undo as RelinkUndoPayload | undefined) ?? null);
      dispatch(
        setAlert({ isOpen: true, msg: t("pages.project.edit.delinkSuccess"), type: "success" }),
      );
      dispatch(closeConfirmationModal(DELINK_MODAL_ID));
      setDelinkSceneInfo(null);
      dispatch(setShouldRerender());
    } catch {
      dispatch(
        setAlert({ isOpen: true, msg: t("pages.project.edit.delinkError"), type: "danger" }),
      );
    }
  };

  const handleUndoRelink = async () => {
    if (!lastUndo || !currentProject) return;
    try {
      await dispatch(
        undoRelink({
          projectId: currentProject.id,
          undo: lastUndo,
          componentId: UNDO_COMPONENT,
        }),
      ).unwrap();
      setLastUndo(null);
      dispatch(
        setAlert({
          isOpen: true,
          msg: t("pages.project.edit.relinkUndoSuccess"),
          type: "success",
        }),
      );
      dispatch(setShouldRerender());
    } catch {
      // Stale payload (links were mutated since) or server error: drop it, a
      // disabled-forever undo button is worse than none.
      setLastUndo(null);
      dispatch(
        setAlert({ isOpen: true, msg: t("pages.project.edit.relinkUndoError"), type: "danger" }),
      );
    }
  };

  const relevantContents = useAppSelector(projectContents).entities;
  const contentsArr = Object.entries(relevantContents).map((e) => {
    return { ...e[1] };
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const sizeRect = useElementRect(containerRef);
  const { getHumanizedNodeNameFromNode } = useNameFormatter();
  const { t } = useTranslation();

  // track if the graph is completely valid (derived, dispatched in an effect
  // below — dispatching from inside the render map was a React anti-pattern)
  const allNodesAreValid = useMemo(() => nodes.every(validateNode), [nodes]);
  useEffect(() => {
    dispatch(setIsValid(allNodesAreValid));
  }, [dispatch, allNodesAreValid]);

  // Language data for the per-language checks (missing/incomplete translation,
  // duplicate rows, broken media ids) — mirrors the Unity editor validator.
  const allPlds = useAppSelector(selectAllGraphPlds);
  const pldsByContentId = useMemo(() => groupPldsByContentId(allPlds), [allPlds]);
  const enabledLanguages = useMemo(() => currentProject?.enabled_languages ?? [], [currentProject]);

  // Every problem found on the displayed nodes, for the "problems" panel.
  // Errors (broken at runtime) first, then warnings. Open/closed state lives
  // in Redux: the ⚠ badge that toggles it sits at page-title level
  // (GraphIssuesBadge), and the panel survives a node-edit round trip.
  const issuesOpen = useAppSelector(getIssuesPanelOpen);
  // `?issues` in the URL opens the panel on arrival (deep link from the
  // publication step). Once per mount.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("issues")) {
      dispatch(setIssuesPanelOpen(true));
    }
  }, []);
  const graphIssues = useMemo(() => {
    const list = collectGraphIssues(nodes, pldsByContentId, enabledLanguages).map(
      ({ node, issue }) => ({
        key: `${nodeUniqueKey(node)}-${issue.messageKey}-${issue.messageParams?.language ?? ""}`,
        severity: issue.severity as GraphIssueSeverity,
        messageKey: issue.messageKey,
        messageParams: issue.messageParams,
        fieldKeys: issue.fieldKeys,
        nodeName: String(node?.details?.name ?? ""),
        typeLabel: formatContentTypeName(node.modelName ?? ""),
        x: node.x,
        y: node.y,
        nodeType: node.type,
      }),
    );
    return list.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1));
  }, [nodes, pldsByContentId, enabledLanguages]);
  const graphIssueErrorCount = useMemo(
    () => graphIssues.filter((i) => i.severity === "error").length,
    [graphIssues],
  );

  // Unlinked scenes are not part of `nodes` (they live in the top strip), so
  // collectGraphIssues never sees them: surface them as their own panel
  // section. Clicking one scrolls the strip to the scene and flashes the ring.
  const unlinkedSceneIssues = useMemo(
    () =>
      (hiddenSceneNodes ?? []).map((hsn, i) => ({
        key: `unlinked-${nodeUniqueKey(hsn)}`,
        nodeName: String(hsn?.details?.name ?? ""),
        typeLabel: formatContentTypeName(hsn.modelName ?? ""),
        stripIndex: i,
      })),
    [hiddenSceneNodes],
  );
  const totalIssueCount = graphIssues.length + unlinkedSceneIssues.length;

  // Feed the page-title ⚠ badge (GraphIssuesBadge). Cleared on unmount so the
  // badge only shows while a graph is actually on screen.
  useEffect(() => {
    dispatch(
      setGraphIssuesSummary({
        errorCount: graphIssueErrorCount,
        warningCount: graphIssues.length - graphIssueErrorCount,
        unlinkedCount: unlinkedSceneIssues.length,
      }),
    );
  }, [dispatch, graphIssues.length, graphIssueErrorCount, unlinkedSceneIssues.length]);
  useEffect(
    () => () => {
      dispatch(setGraphIssuesSummary(null));
    },
    [dispatch],
  );

  // We build and array of all the nodes in the graph.
  // the "graphBuilder" did the hard work of computing the positions, so now we just have to select the right React
  // component per node type and set it's x/y values.

  // Also, let's store the bottom-most element while we're at it. It will be useful later, to position the "addScene" <rect>
  let bottomMostNode = { y: 0 };

  // Interactions that EXIST under a scene (parent) but aren't wired into THAT
  // scene's local flow (not reachable from the scene via actions → invisible in
  // the graph). Computed per scene — visible AND orphan — and surfaced as a badge.
  const allContents = useAppSelector(selectAllGraphContents);
  const allContentTypes = useAppSelector(selectAllGraphContentTypes);
  const allActions = useAppSelector(selectAllGraphActions);
  const unlinkedBySceneContentId = useMemo(() => {
    const ctByContentId = new Map<number, any>();
    for (const ct of allContentTypes) {
      if (ct?.object?.content != null) ctByContentId.set(Number(ct.object.content), ct);
    }
    const isSceneContent = (id: number) =>
      ctByContentId.get(Number(id))?.modelName === "content-scene";

    // directChildren via actions (any kind): affect_content -> affected_content
    const directChildren = new Map<number, Set<number>>();
    for (const a of allActions) {
      const src = Number(a.affect_content);
      const dst = Number(a.affected_content);
      if (!src || !dst || src === dst) continue;
      const set = directChildren.get(src) ?? new Set<number>();
      set.add(dst);
      directChildren.set(src, set);
    }
    // reachable interactions from a scene (mirror buildGraph's findLinkedAssets)
    const reachableCache = new Map<number, Set<number>>();
    const reachableAssets = (sceneContentId: number): Set<number> => {
      const cached = reachableCache.get(sceneContentId);
      if (cached) return cached;
      const result = new Set<number>();
      const visited = new Set<number>([sceneContentId]);
      const queue = [sceneContentId];
      while (queue.length > 0) {
        const cur = queue.shift() as number;
        for (const child of Array.from(directChildren.get(cur) ?? [])) {
          if (visited.has(child)) continue;
          visited.add(child);
          if (isSceneContent(child)) continue; // don't cross into scenes
          result.add(child);
          queue.push(child);
        }
      }
      reachableCache.set(sceneContentId, result);
      return result;
    };

    const sceneContentIds = new Set<number>();
    for (const n of nodes) if (n.type === "scene") sceneContentIds.add(Number(n.contentId));
    for (const h of hiddenSceneNodes ?? []) sceneContentIds.add(Number(h.contentId));

    const result = new Map<
      number,
      { contentId: number; modelName: string; name: string; ctId: number }[]
    >();
    for (const c of allContents) {
      const parentId = Number(c.parent);
      if (!sceneContentIds.has(parentId)) continue;
      const ct = ctByContentId.get(Number(c.id));
      if (!ct || !isAssetContentType(ct)) continue;
      if (reachableAssets(parentId).has(Number(c.id))) continue;
      const list = result.get(parentId) ?? [];
      list.push({
        contentId: Number(c.id),
        modelName: ct.modelName,
        name: ct.object?.name ?? (c as any).name ?? "",
        ctId: Number(ct.object?.id),
      });
      result.set(parentId, list);
    }
    return result;
  }, [allContents, allContentTypes, allActions, nodes, hiddenSceneNodes]);

  // --- graph search: grey out non-matches, Enter cycles + centers matches ---
  const [graphSearch, setGraphSearch] = useState("");
  // Type filter: normalized modelNames the user restricts to. Empty = no type
  // restriction. Combined with the text search via AND.
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [typesOpen, setTypesOpen] = useState(false);
  // -1 = not cycling yet; first Enter lands on match 0.
  const [searchFocusIdx, setSearchFocusIdx] = useState(-1);
  useEffect(() => {
    setSearchFocusIdx(-1);
  }, [graphSearch, selectedTypes]);

  const normalizeModelName = (modelName: string | undefined | null): string =>
    String(modelName ?? "")
      .toLowerCase()
      .replace(/-/g, "_");

  // Distinct content types actually present in the graph, for the filter
  // dropdown — scenes first, then alphabetical by label. Empty buckets are
  // never offered, so the list stays as small as the scenario allows.
  const presentTypes = useMemo(() => {
    const byKey = new Map<string, { key: string; label: string; count: number }>();
    const add = (modelName: string | undefined | null) => {
      const key = normalizeModelName(modelName);
      if (!key) return;
      const entry = byKey.get(key) ?? {
        key,
        label: formatContentTypeName(modelName),
        count: 0,
      };
      entry.count += 1;
      byKey.set(key, entry);
    };
    for (const n of nodes) add(n.modelName);
    for (const h of hiddenSceneNodes ?? []) add(h.modelName);
    const sceneKey = "content_scene";
    return Array.from(byKey.values()).sort((a, b) => {
      if (a.key === sceneKey) return -1;
      if (b.key === sceneKey) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [nodes, hiddenSceneNodes]);

  const search = useMemo(() => {
    const q = normalizeForSearch(graphSearch.trim());
    const hasText = q.length > 0;
    const hasType = selectedTypes.size > 0;
    if (!hasText && !hasType) return null;

    const matchedKeys = new Set<string>();
    const contextSceneContentIds = new Set<number>();
    // Viewport matches only (cyclable with Enter); unlinked-panel scenes are
    // dimmed + counted but not cyclable (they live outside the viewport).
    const matches: { x: number; y: number; type: string }[] = [];

    const parentByContentId = new Map<number, number>();
    for (const c of contentsArr) {
      if ((c as any).parent != null) {
        parentByContentId.set(Number((c as any).id), Number((c as any).parent));
      }
    }

    // A node matches when it passes the text search AND the type filter (each
    // is a no-op when inactive).
    const typeOk = (modelName: string | undefined | null) =>
      !hasType || selectedTypes.has(normalizeModelName(modelName));

    for (const node of nodes) {
      if (!typeOk(node.modelName)) continue;
      // Text search matches NAMES only (raw + humanized for scenes) — type
      // matching lives in the dedicated Types filter.
      const haystack = [
        node?.details?.name ?? "",
        node.type === "scene" ? getHumanizedNodeNameFromNode(node) : "",
      ]
        .map((l) => normalizeForSearch(String(l)))
        .join(" ");
      if (hasText && !haystack.includes(q)) continue;
      matchedKeys.add(nodeUniqueKey(node));
      matches.push({ x: node.x, y: node.y, type: node.type });
      if (node.type === "asset") {
        const hostSceneContentId = parentByContentId.get(Number(node.contentId));
        if (hostSceneContentId != null) contextSceneContentIds.add(hostSceneContentId);
      }
    }

    for (const hsn of hiddenSceneNodes ?? []) {
      if (!typeOk(hsn.modelName)) continue;
      const name = String(hsn?.details?.name ?? "");
      const haystack = normalizeForSearch(`${name} ${humanizeNodeName(name)}`);
      if (!hasText || haystack.includes(q)) matchedKeys.add(nodeUniqueKey(hsn));
    }

    return { matchedKeys, contextSceneContentIds, matches };
  }, [
    graphSearch,
    selectedTypes,
    nodes,
    hiddenSceneNodes,
    contentsArr,
    getHumanizedNodeNameFromNode,
  ]);

  const toggleType = (key: string) =>
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Zero matches greys the WHOLE graph: nothing lit = instant "no hit here"
  // feedback, consistent with how partial matches dim the rest.
  const searchDimming = search;
  const nodeSearchOpacity = (node: any): number => {
    if (!searchDimming) return 1;
    if (searchDimming.matchedKeys.has(nodeUniqueKey(node))) return 1;
    // Host scene of a matching interaction: kept readable for context.
    if (node.type === "scene" && searchDimming.contextSceneContentIds.has(Number(node.contentId))) {
      return 0.55;
    }
    return 0.18;
  };

  // Transient focus ring: flashes around the node just centered by Enter on
  // the search or a click in the issues panel, so the eye lands on it without
  // scanning. `seq` re-mounts the rect to restart the animation when the same
  // node is targeted twice in a row.
  const [focusRing, setFocusRing] = useState<{
    x: number;
    y: number;
    type: string;
    seq: number;
  } | null>(null);
  useEffect(() => {
    if (!focusRing) return undefined;
    const timer = setTimeout(() => setFocusRing(null), 2000);
    return () => clearTimeout(timer);
  }, [focusRing]);
  // Visual centers/radii derived from the node SVGs, NOT the layout boxes:
  // scene = 150×150 svg, viewBox 0 0 100 120 (meet → ×1.25, x-offset 12.5),
  // circle (50,60) r39.5 → center (75,75) r≈49. Asset = 94×94 svg, viewBox
  // 0 0 50 50 (×1.88), circle center (26,26) r19 → center (48.9,48.9) r≈35.7.
  const focusRingElement = focusRing ? (
    <circle
      key={`focus-ring-${focusRing.seq}`}
      className="graph-focus-ring"
      cx={focusRing.x + (focusRing.type === "scene" ? 75 : 48.9)}
      cy={focusRing.y + (focusRing.type === "scene" ? 75 : 48.9)}
      r={focusRing.type === "scene" ? 58 : 43}
      fill="none"
      stroke="#02FCA5"
      strokeWidth={3}
      pointerEvents="none"
    />
  ) : null;

  // Same transient ring, but for the unlinked-scenes strip: the chips live in
  // their own <svg>, so the ring is drawn there, at the chip's slot.
  const stripScrollRef = useRef<HTMLDivElement>(null);
  const [stripFocusRing, setStripFocusRing] = useState<{ index: number; seq: number } | null>(null);
  useEffect(() => {
    if (!stripFocusRing) return undefined;
    const timer = setTimeout(() => setStripFocusRing(null), 2000);
    return () => clearTimeout(timer);
  }, [stripFocusRing]);

  const nodeElements = useMemo(
    () =>
      nodes.map((node) => {
        if (bottomMostNode.y < node.y) bottomMostNode = node;

        const contentId = node?.details?.content;
        const relevantC = contentsArr.find((c: any) => {
          return Number(c.id) === Number(contentId);
        });

        // `duplicatedForSceneId` is set on render-only clones of an asset
        // shared by >1 scene; include it so the clone gets its own React key
        // (a colliding key would make React drop it — the original gap bug).
        const uniqueKey = nodeUniqueKey(node);
        const valid = validateNode(node);
        const editable = isNodeEditable(node);
        const { x, y } = node;

        switch (node.type) {
          case "scene":
            // eslint-disable-next-line no-case-declarations
            const title = getHumanizedNodeNameFromNode(node);
            return (
              <g
                key={uniqueKey}
                opacity={nodeSearchOpacity(node)}
                style={{ transition: "opacity 150ms" }}
              >
                <SVGNodeScene
                  title={title}
                  valid={valid}
                  x={x}
                  y={y}
                  onClick={() => {
                    navigate(`node/${node.modelName}/${node.details.id}`);
                  }}
                  content={relevantC}
                  ctName={node?.details?.name}
                  details={node?.details}
                  sceneType={node?.details?.scene_type}
                  cursor="pointer"
                  startScene={node?.details?.is_start_scene}
                  className="tour-scene-node"
                  onCopyScene={handleCopyScene}
                  onDuplicateScene={handleDuplicateScene}
                  onRelinkScene={handleRelinkScene}
                  onCreateLinkedScene={handleCreateLinkedScene}
                  onDelinkScene={handleDelinkScene}
                  onDeleteScene={handleDeleteScene}
                  onSetStartScene={handleSetStartScene}
                  unlinkedInteractions={unlinkedBySceneContentId.get(Number(node.contentId)) || []}
                  onEditUnlinked={(info: { modelName: string; ctId: number }) =>
                    navigate(`node/${info.modelName}/${info.ctId}`)
                  }
                />
              </g>
            );
          case "asset":
            // eslint-disable-next-line no-case-declarations
            const type = detectTypeForAssetNode(node);
            return (
              <g
                key={uniqueKey}
                opacity={nodeSearchOpacity(node)}
                style={{ transition: "opacity 150ms" }}
              >
                <SVGNodeAsset
                  type={type}
                  valid={valid}
                  disabled={!editable}
                  selected={selectedContentId === node.contentId}
                  x={x}
                  y={y}
                  modelName={node.modelName}
                  onClick={() => {
                    if (editable) {
                      navigate(`node/${node.modelName}/${node.details.id}`);
                    }
                  }}
                  content={relevantC}
                  ctName={node?.details?.name}
                  cursor="pointer"
                />
              </g>
            );
          default:
            return null;
        }
      }),

    [nodes, unlinkedBySceneContentId, searchDimming],
  );

  const width = Math.max((sizeRect?.width || 0) - 100, 0);
  const height = sizeRect?.height || 0;
  const tx = (width - layout.width) / 2;
  const ty = (height - layout.height) / 2;
  const viewBox = `0 0 ${layout.width} ${layout.height}`;

  const unlinkedSceneNodes = hiddenSceneNodes?.map((hsn, i) => {
    const contentId = hsn?.contentId;
    const relevantC = contentsArr.find((c: any) => {
      return Number(c.id) === Number(contentId);
    });
    const { x } = hsn;
    const title = hsn.details.name;
    const humanizedTitle = humanizeNodeName(title);
    const hsnOpacity =
      !searchDimming || searchDimming.matchedKeys.has(nodeUniqueKey(hsn)) ? 1 : 0.18;
    return (
      <g key={i} opacity={hsnOpacity} style={{ transition: "opacity 150ms" }}>
        <SVGNodeScene
          title={humanizedTitle}
          valid={false}
          x={x + 150 * i}
          content={relevantC}
          ctName={hsn?.details?.name}
          details={hsn?.details}
          sceneType="video"
          cursor="pointer"
          unlinked
          hideInfo
          onClick={() => {
            navigate(`node/${hsn.modelName}/${hsn.details.id}`);
          }}
          onCopyScene={handleCopyScene}
          onDuplicateScene={handleDuplicateScene}
          onRelinkScene={handleRelinkScene}
          onDeleteScene={handleDeleteScene}
          unlinkedInteractions={unlinkedBySceneContentId.get(Number(hsn.contentId)) || []}
          onEditUnlinked={(info: { modelName: string; ctId: number }) =>
            navigate(`node/${info.modelName}/${info.ctId}`)
          }
        />
      </g>
    );
  });

  // Candidate source scenes for relinking = every scene currently in the graph.
  const sceneOptions = nodes
    .filter((n): n is NodeScene => n.type === "scene")
    .map((n) => ({
      contentSceneId: Number(n.details.id),
      contentId: Number(n.details.content),
      label: getHumanizedNodeNameFromNode(n),
      sceneType: n.details.scene_type,
      isStartScene: Boolean(n.details.is_start_scene),
      interactionCount: n.assets.size,
    }));

  const UnlinkedScenesWrapper = () => {
    const numberOfUnlinkedScenes = unlinkedSceneNodes?.length;
    return (
      <svg
        style={{
          visibility: Number(numberOfUnlinkedScenes) <= 0 ? "hidden" : "visible",
        }}
        viewBox={`0 0 ${Number(numberOfUnlinkedScenes) * 150} 150`}
        height={"100%"}
      >
        <g>{unlinkedSceneNodes}</g>
        {stripFocusRing && (
          <circle
            key={`strip-focus-ring-${stripFocusRing.seq}`}
            className="graph-focus-ring"
            cx={
              (hiddenSceneNodes?.[stripFocusRing.index]?.x ?? 0) + stripFocusRing.index * 150 + 75
            }
            cy={75}
            r={58}
            fill="none"
            stroke="#02FCA5"
            strokeWidth={3}
            pointerEvents="none"
          />
        )}
      </svg>
    );
  };

  /* React.useEffect(() => {
    if (!allNodesAreValid) {
      //@todo create an alert slide
      alert({ isOpen: true, msg: "mediaMissing", type: "warning" });
      //dispatch(setAlert({ isOpen: true, msg: "mediaMissing", type: "warning" }));
    }
  }, [projectContents]);*/
  // Draw the lines between nodes, and assigning them an unique (but stable) key
  const lineElements = links.map(({ from, to, linksToScene, index, isNotThatSimple = false }) => {
    const uniqueKey = `${from.modelName}-${from.contentId}-to-${to.modelName}-${to.contentId}`;
    // A link stays lit only when BOTH its endpoints match the search: with a
    // sparse result set that dims almost every edge, which is the point.
    const lineOpacity =
      !searchDimming ||
      (searchDimming.matchedKeys.has(nodeUniqueKey(from)) &&
        searchDimming.matchedKeys.has(nodeUniqueKey(to)))
        ? 1
        : 0.15;
    return (
      <g key={uniqueKey} opacity={lineOpacity} style={{ transition: "opacity 150ms" }}>
        <Line
          from={from}
          to={to}
          linksToScene={linksToScene}
          index={index}
          isNotThatSimple={isNotThatSimple}
        />
      </g>
    );
  });

  // Below we render the SVG component.
  // this is a bit convoluted since our logic is either:
  // ➡︎ to display the graph centered if the canvas is larger than the graph
  // ➡︎ otherwise if the graph is too big we want to resize the graph (we set height/width but keep viewBox)

  const minScale = 1;
  // 150 => scene node width / 500 => marge
  const maxScale = tx >= 0 && ty >= 0 ? 3 : layout.width / (150.0 + 500.0);

  const persistedView = useAppSelector(getGraphView);
  const persistedMatchesProject =
    currentProject?.id != null && persistedView.projectId === currentProject.id;

  const [graphScale, setGraphScale] = useState(
    persistedMatchesProject ? persistedView.scale : minScale,
  );
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [graphTranslateX, setGraphTranslateX] = useState(
    persistedMatchesProject ? persistedView.translateX : 0,
  );
  const [graphTranslateY, setGraphTranslateY] = useState(
    persistedMatchesProject ? persistedView.translateY : 0,
  );

  // Persist zoom/pan to Redux so it survives navigating into NodeForm and back.
  // Debounced to avoid dispatching on every mousemove frame.
  useEffect(() => {
    if (currentProject?.id == null) return undefined;
    const handle = window.setTimeout(() => {
      dispatch(
        setGraphView({
          projectId: currentProject.id,
          scale: graphScale,
          translateX: graphTranslateX,
          translateY: graphTranslateY,
        }),
      );
    }, 150);
    return () => window.clearTimeout(handle);
  }, [dispatch, currentProject?.id, graphScale, graphTranslateX, graphTranslateY]);

  const svgElement =
    tx >= 0 && ty >= 0 ? (
      // SVG is smaller than the available screen space: draw it centered
      <div
        style={{
          // Center the svg horizontally: the scale/translate transform-origin
          // is this div's center, and centerOnMatch's translate math assumes
          // that point coincides with the svg's center (width/2). Left-aligning
          // the svg breaks that assumption and, amplified by the CSS scale,
          // throws focus() far off-center.
          display: "flex",
          justifyContent: "center",
          transform: `scale(${graphScale}) translate(${graphTranslateX}px, ${graphTranslateY}px)`,
        }}
      >
        <svg
          key="svg-container"
          id="graph-svg"
          className="tour-graph .graph-svg"
          style={{
            backgroundColor: "inherit",
          }}
          height={height}
          width={width}
          alignmentBaseline="middle"
          textAnchor="middle"
        >
          <g transform={`translate(${tx} ${ty})`}>
            {lineElements}
            {nodeElements}
            {focusRingElement}
          </g>
        </svg>
      </div>
    ) : (
      // SVG does not fit: scale it
      // (NOTE: SVG rendering will keep it centered)
      <div
        style={{
          // See fit-branch comment: centering the svg keeps the transform
          // origin on the svg center so centerOnMatch lands the node dead-on.
          display: "flex",
          justifyContent: "center",
          transform: `scale(${graphScale}) translate(${graphTranslateX}px, ${graphTranslateY}px)`,
        }}
      >
        <svg
          key="svg-container"
          id="graph-svg"
          className="tour-graph .graph-svg"
          style={{
            backgroundColor: "inherit",
          }}
          height={height} //"100%"
          width={width} // this slightly improves graph scaling
          viewBox={viewBox}
          alignmentBaseline="middle"
          textAnchor="middle"
          overflow="auto"
        >
          <g>
            {lineElements}
            {nodeElements}
            {focusRingElement}
          </g>
        </svg>
      </div>
    );

  const setScale = (newScale: number) => {
    if (newScale > maxScale) setGraphScale(maxScale);
    else if (newScale < minScale) setGraphScale(minScale);
    else setGraphScale(newScale);

    setTranslate(graphTranslateX, graphTranslateY);
  };

  const onWheel = (event: any) => {
    setScale(event.deltaY * -0.01 + graphScale);
  };

  const onMouseDown = () => {
    setIsMouseDown(true);
  };

  const onMouseUp = () => {
    setIsMouseDown(false);
  };

  const setTranslate = (newGraphTranslateX: number, newGraphTranslateY: number) => {
    const margeX = 0;
    const margeY = 0;

    const w = width / 2 - margeX;
    if (newGraphTranslateX > w - margeX) {
      setGraphTranslateX(w - margeX);
    } else if (newGraphTranslateX < -w) {
      setGraphTranslateX(-w);
    } else {
      setGraphTranslateX(newGraphTranslateX);
    }

    const h = height / 2;
    if (newGraphTranslateY > h - margeY) {
      setGraphTranslateY(h - margeY);
    } else if (newGraphTranslateY < -h + margeY) {
      setGraphTranslateY(-h + margeY);
    } else {
      setGraphTranslateY(newGraphTranslateY);
    }
  };

  const onMouseMove = (event: any) => {
    if (isMouseDown) {
      const factor = 2;
      const newGraphTranslateX = graphTranslateX + (event.movementX * factor) / graphScale;
      const newGraphTranslateY = graphTranslateY + (event.movementY * factor) / graphScale;

      setTranslate(newGraphTranslateX, newGraphTranslateY);
    }
  };

  const reinitZoom = () => {
    setGraphTranslateX(0);
    setGraphTranslateY(0);
    setGraphScale(minScale);
  };

  const zoomButtonStep = maxScale / 10.0;
  const onZoomInButton = () => setScale(graphScale + zoomButtonStep);
  const onZoomOutButton = () => setScale(graphScale - zoomButtonStep);

  // Center the viewport on one search match, zooming in enough that the node is
  // actually readable. The wrapper div is `scale(S) translate(T)` about its own
  // center C, so a layout point rendered at wrapper-pixel p lands on screen at
  // C + S·(p + T − C); to bring it to C, T = C − p — independent of S. We set
  // scale and translate directly (setScale/setTranslate re-clamp for dragging,
  // which would fight a precise recenter on a wide graph).
  // offsetX shifts the landing point: with the issues side panel open, "center"
  // means the center of the area the panel doesn't cover.
  const centerOnMatch = (m: { x: number; y: number; type: string }, offsetX = 0) => {
    // Visual center of the node in layout units — MUST match the offsets used
    // by focusRingElement (scene 75/75, asset 48.9/48.9), which are derived
    // from the node SVGs, not their layout boxes. Using the layout box halves
    // here (the old nodeW/2, nodeH/2) put the viewport on the wrong point.
    const centerX = m.type === "scene" ? 75 : 48.9;
    const centerY = m.type === "scene" ? 75 : 48.9;
    // On-screen width we aim for the node, in layout units (scene ≈ 150).
    const zoomBasisW = m.type === "scene" ? 150 : 60;
    // intrinsicScale = layout→wrapper-pixel factor before the CSS scale.
    // Fit branch draws 1:1 from (tx,ty); overflow branch scales via viewBox
    // (xMidYMid meet) so the whole width fits.
    const fit = tx >= 0 && ty >= 0;
    const intrinsicScale = fit ? 1 : Math.min(width / layout.width, height / layout.height);
    const offX = fit ? tx : (width - layout.width * intrinsicScale) / 2;
    const offY = fit ? ty : (height - layout.height * intrinsicScale) / 2;
    const px = offX + (m.x + centerX) * intrinsicScale;
    const py = offY + (m.y + centerY) * intrinsicScale;

    // Aim for a scene node ~220px wide on screen (total scale = S·intrinsic),
    // clamped to the graph's own zoom bounds. Never zoom back OUT below the
    // user's current scale — only push in when needed.
    const targetTotal = 220 / zoomBasisW;
    const desiredScale = Math.min(Math.max(targetTotal / intrinsicScale, graphScale), maxScale);
    setGraphScale(desiredScale);
    // The wrapper is `scale(S) translate(T)`: a screen-pixel offset must be
    // divided by S to survive the outer scale.
    setGraphTranslateX(width / 2 - px + offsetX / desiredScale);
    setGraphTranslateY(height / 2 - py);
    setFocusRing((prev) => ({ x: m.x, y: m.y, type: m.type, seq: (prev?.seq ?? 0) + 1 }));
  };

  // Bring one unlinked scene into view: scroll the strip so the chip lands at
  // the center of the visible area (same offsetX convention as centerOnMatch:
  // with the issues panel open, "center" excludes the covered part), then
  // flash the ring on it.
  const focusUnlinkedScene = (index: number, offsetX = 0) => {
    const el = stripScrollRef.current;
    const count = hiddenSceneNodes?.length ?? 0;
    if (el && count > 0) {
      const chipCenter = ((index * 150 + 75) / (count * 150)) * el.scrollWidth;
      el.scrollTo({ left: chipCenter - el.clientWidth / 2 - offsetX, behavior: "smooth" });
    }
    setStripFocusRing((prev) => ({ index, seq: (prev?.seq ?? 0) + 1 }));
  };

  // Deep link from the publication step: ?focusContent=<contentId> centers
  // the graph on that content's node (and flashes the focus ring) once the
  // nodes and the viewport are ready. Handled once per mount.
  const focusContentHandledRef = useRef(false);
  useEffect(() => {
    if (focusContentHandledRef.current) return;
    const focusContent = Number(new URLSearchParams(window.location.search).get("focusContent"));
    if (!focusContent) {
      focusContentHandledRef.current = true;
      return;
    }
    if (!width || nodes.length === 0) return;
    const node = nodes.find(
      (n) =>
        Number(n.contentId) === focusContent && !(n.type === "asset" && n.duplicatedForSceneId),
    );
    const panelOffset = issuesOpen && totalIssueCount > 0 ? -192 : 0;
    if (node) {
      // Same half-panel shift as the issues panel's own clicks.
      centerOnMatch({ x: node.x, y: node.y, type: node.type }, panelOffset);
    } else {
      // Not on the canvas: an unlinked scene lives in the top strip.
      const stripIndex = (hiddenSceneNodes ?? []).findIndex(
        (h) => Number(h.contentId) === focusContent,
      );
      if (stripIndex >= 0) focusUnlinkedScene(stripIndex, panelOffset);
    }
    focusContentHandledRef.current = true;
  }, [nodes, width]);

  const cycleSearchFocus = (dir: 1 | -1) => {
    const cyclable = search?.matches ?? [];
    if (cyclable.length === 0) return;
    const next =
      ((((searchFocusIdx < 0 && dir === -1 ? 0 : searchFocusIdx) + dir) % cyclable.length) +
        cyclable.length) %
      cyclable.length;
    setSearchFocusIdx(next);
    centerOnMatch(cyclable[next]);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      cycleSearchFocus(e.shiftKey ? -1 : 1);
    } else if (e.key === "Escape") {
      setGraphSearch("");
      (e.target as HTMLInputElement).blur();
    }
  };

  // Enter cycles the matches even when the search input isn't focused — e.g.
  // right after picking a type in the filter. Keystrokes aimed at a form
  // control or a button are left alone (the search input has its own handler
  // above, and Enter on a focused button must stay a click).
  useEffect(() => {
    if (!search || search.matches.length === 0) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON") return;
      if (target?.isContentEditable) return;
      e.preventDefault();
      cycleSearchFocus(e.shiftKey ? -1 : 1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [search, searchFocusIdx, typesOpen]);
  const onRerenderGraph = () => {
    // Explicit user action: opt into the full reset+spinner "clean refresh".
    // setShouldRerender re-triggers EditProjectScenes' effect; the one-shot
    // flag tells loadGraphFct to take the visible (non-silent) path.
    dispatch(requestGraphFullReload());
    dispatch(setShouldRerender());
  };
  return (
    <div
      ref={containerRef}
      id="graph-div"
      className={classNames(
        "graph",
        {
          allValid: allNodesAreValid,
        },
        "overflow-hidden",
      )}
    >
      {/* Silent-refetch / duplicate-in-flight overlay: a non-blocking spinner
          centered over the graph (#graph-div is position:relative).
          pointer-events-none keeps the optimistic graph underneath fully
          interactive while it loads. */}
      {(isSilentRefetching || isDuplicatingScene) && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <Spinner relative />
        </div>
      )}
      {Number(unlinkedSceneNodes?.length) > 0 && (
        <div className="flex self-start mr-28 bg-gray-200 border border-gray-200 rounded-3xl h-[19.666667%] mb-6 pb-2 w-full pl-2">
          <span className="flex items-center w-40">
            <p>
              {Number(unlinkedSceneNodes?.length) === 0
                ? ""
                : Number(unlinkedSceneNodes?.length) === 1
                ? t("pages.project.edit.unlinkedScene")
                : t("pages.project.edit.unlinkedScenes")}
            </p>
            :
          </span>
          <div
            ref={stripScrollRef}
            className={"overflow-y-auto overflow-x-auto w-full overscroll-x-auto "}
            style={{ width: "calc(100% - 11rem)" }}
          >
            <UnlinkedScenesWrapper />
          </div>
        </div>
      )}
      <div
        className={`${
          Number(unlinkedSceneNodes?.length) > 0 ? "h-5/6" : "h-full"
        } overflow-hidden bg-gray-200 border border-gray-200 rounded-3xl`}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseUp}
      >
        <div className="z-10 m-2 absolute flex w-full justify-center h-[5%] min-h-[2.0rem] cursor-grabbing">
          {/* Search: greys out non-matching nodes; Enter cycles + centers on
              matches. Mouse events stay local so typing/clicking here never
              pans or zooms the graph underneath. */}
          <div
            className="flex items-center cursor-default"
            onMouseDown={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <SearchInput
                value={graphSearch}
                onChange={setGraphSearch}
                onKeyDown={onSearchKeyDown}
                placeholder={t("pages.project.edit.graphSearchPlaceholder")}
                className="w-64"
              />
              {/* Result counter floats under the search field: it takes no
                  toolbar space, so nothing is reserved (= no hole) when
                  there's no active search/filter. */}
              {search && (
                <span className="absolute left-1 top-full mt-1 text-xs text-gray-600 whitespace-nowrap">
                  {searchFocusIdx >= 0 && search.matches.length > 0
                    ? `${(searchFocusIdx % search.matches.length) + 1}/${search.matches.length}`
                    : t("pages.project.edit.graphSearchResults", {
                        count: search.matchedKeys.size,
                      })}
                </span>
              )}
            </div>

            {/* Type filter: multi-select of the types present in this graph,
                ANDed with the text search. Only types actually on screen are
                offered, so the list stays small. */}
            <div className="relative ml-2">
              <button
                type="button"
                onClick={() => setTypesOpen((v) => !v)}
                // Fixed width + justify-between so the count badge slots in
                // between the label and the caret without ever reflowing the
                // toolbar (label stays left-anchored, caret stays right-anchored).
                className={`flex items-center justify-between gap-1 w-28 px-3 py-2.5 border rounded-lg text-sm bg-white transition-colors ${
                  selectedTypes.size > 0
                    ? "border-green-500 text-green-700 font-medium"
                    : "border-gray-300 text-gray-700"
                }`}
              >
                <span>{t("pages.project.edit.graphTypeFilter")}</span>
                <span className="flex items-center gap-1">
                  {selectedTypes.size > 0 && (
                    // Quick clear: resets the type filter without opening the
                    // dropdown. Lives inside the fixed-width pill so nothing
                    // in the toolbar ever shifts.
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTypes(new Set());
                      }}
                      title={t("pages.project.edit.graphTypeClear")}
                      className="badge-sucess text-[10px] gap-0.5 hover:bg-green-400 cursor-pointer"
                    >
                      {selectedTypes.size}
                      <span className="font-bold">×</span>
                    </span>
                  )}
                  {/* One glyph flipped in CSS: the ▴/▾ pair renders at different
                      sizes (different font fallbacks), making the caret "jump". */}
                  <span className={`inline-block text-xs ${typesOpen ? "rotate-180" : ""}`}>▾</span>
                </span>
              </button>
              {typesOpen && (
                <>
                  {/* click-away catcher */}
                  <div className="fixed inset-0 z-10" onClick={() => setTypesOpen(false)} />
                  <div className="absolute z-20 mt-1 left-0 w-60 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                    {presentTypes.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400">
                        {t("pages.project.edit.graphNoType")}
                      </div>
                    ) : (
                      <>
                        {presentTypes.map((ty) => (
                          <label
                            key={ty.key}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTypes.has(ty.key)}
                              onChange={() => toggleType(ty.key)}
                            />
                            <span className="flex-1 text-gray-900">{ty.label}</span>
                            <span className="text-xs text-gray-400">{ty.count}</span>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="px-2 w-'4">
            <div className="border-l border-gray-500 h-10 "></div>
          </div>
          <button
            className="btn-primary-fill ml-1 min-w-0 rounded-full flex items-center w-24"
            onClick={onRerenderGraph}
          >
            <Icon icon={"RefreshIcon"} className={"h-full w-full stroke-white ml-0.5"} />
          </button>
          <div className="px-2 w-'4">
            <div className="border-l border-gray-500 h-10 "></div>
          </div>
          <button className="btn-primary-fill pb-0 pt-0 w-44" onClick={reinitZoom}>
            {t("pages.project.edit.resetZoom")}
          </button>

          <button
            className="btn-primary-fill ml-1 min-w-0 flex items-center w-24"
            disabled={graphScale <= minScale}
            onClick={onZoomOutButton}
          >
            <Icon icon={"ZoomOutIcon"} className={"h-full w-full stroke-white"} />
          </button>

          <button
            className="btn-primary-fill ml-1 min-w-0 flex items-center w-24"
            disabled={graphScale >= maxScale}
            onClick={onZoomInButton}
          >
            <Icon icon={"ZoomInIcon"} className={"h-full w-full stroke-white"} />
          </button>

          {/* The ⚠ problems badge lives at page-title level (GraphIssuesBadge):
              its scope is the whole scenario, not just this canvas. */}

          {lastUndo && (
            <>
              <div className="px-2 w-'4">
                <div className="border-l border-gray-500 h-10 "></div>
              </div>
              <button className="btn-alternative-outline pb-0 pt-0 w-56" onClick={handleUndoRelink}>
                {t("pages.project.edit.relinkUndoButton")}
              </button>
            </>
          )}

          {/* <div
            className="absolute right-[5rem] flex items-center"
            style={{
              cursor: "pointer",
            }}
          >
            <Tooltip
              message={t("pages.project.edit.gotoStudioNoob")}
              forceSingleLine={false}
              classNameChildren="w-64 right-20"
            >
              <Icon
                icon={"LightBulbIcon"}
                className="h-10 w-10 text-yellow-400 transition-transform duration-300 ease-in-out hover:scale-110 hover:text-yellow-500"
                fill="#facc15"
              />
            </Tooltip>
          </div> */}
        </div>
        <div className="z-0 cursor-grabbing">{svgElement}</div>
      </div>

      {/* Issues side panel: full PAGE height (fixed, like the publication
          view's panel — above the navbar z-50, below the toasts z-[1000]),
          matching the badge's module-wide scope. Stays open while the user
          works through the list — each click centers the graph on the culprit
          in the area the panel doesn't cover. */}
      {issuesOpen && totalIssueCount > 0 && (
        <div
          className="fixed inset-y-0 right-0 z-[500] w-96 bg-white border-l border-gray-200 shadow-lg flex flex-col"
          onMouseDown={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
            <div>
              <p className="text-sm font-semibold text-gray-700">
                {t("pages.project.edit.graphIssuesTitle")}
              </p>
              <p className="text-[11px] text-gray-400">{t("pages.project.edit.graphIssuesHint")}</p>
            </div>
            <button
              type="button"
              onClick={() => dispatch(setIssuesPanelOpen(false))}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1"
              aria-label={t("general.close")}
            >
              ×
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {(["error", "warning"] as const).map((severity) => {
              const items = graphIssues.filter((i) => i.severity === severity);
              if (items.length === 0) return null;
              return (
                <div key={severity} className="py-1">
                  <p
                    className={`px-4 py-1 text-[11px] font-semibold uppercase ${
                      severity === "error" ? "text-red-600" : "text-amber-600"
                    }`}
                  >
                    {t(
                      severity === "error"
                        ? "pages.project.edit.graphIssueErrors"
                        : "pages.project.edit.graphIssueWarnings",
                    )}{" "}
                    ({items.length})
                  </p>
                  {items.map((issue) => (
                    <button
                      key={issue.key}
                      className="group flex w-full items-start gap-2 px-4 py-1.5 text-sm text-gray-900 hover:bg-gray-100"
                      onClick={() =>
                        // Shift by half the panel width so the node lands in
                        // the middle of the uncovered area.
                        centerOnMatch({ x: issue.x, y: issue.y, type: issue.nodeType }, -192)
                      }
                    >
                      <span
                        className={`flex-shrink-0 text-xs ${
                          severity === "error" ? "text-red-500" : "text-amber-500"
                        }`}
                      >
                        ⚠
                      </span>
                      {/* Two lines: name + type on top, full message below —
                          the message wraps over as many lines as needed, a
                          truncated problem is a useless one. */}
                      <span className="flex-1 min-w-0">
                        <span className="flex items-baseline justify-between gap-2">
                          <span
                            className="truncate text-left group-hover:text-green-600"
                            title={issue.nodeName || issue.typeLabel}
                          >
                            {issue.nodeName || issue.typeLabel}
                          </span>
                          <span className="text-[11px] text-gray-400 flex-shrink-0">
                            {issue.typeLabel}
                          </span>
                        </span>
                        <span className="block text-left text-xs text-gray-500 whitespace-normal break-words">
                          {t(
                            `pages.project.edit.graphIssue.${issue.messageKey}`,
                            issue.fieldKeys?.length
                              ? {
                                  ...issue.messageParams,
                                  fields: issue.fieldKeys
                                    .map((k) => prettyLabel(k, t as any))
                                    .join(", "),
                                }
                              : issue.messageParams,
                          )}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
            {/* Unlinked scenes get their own section: they aren't node
                problems, and clicking one focuses the strip chip instead of
                a canvas node. */}
            {unlinkedSceneIssues.length > 0 && (
              <div className="py-1">
                <p className="px-4 py-1 text-[11px] font-semibold uppercase text-amber-600">
                  {t("pages.project.edit.graphIssueUnlinked")} ({unlinkedSceneIssues.length})
                </p>
                {unlinkedSceneIssues.map((issue) => (
                  <button
                    key={issue.key}
                    className="group flex w-full items-start gap-2 px-4 py-1.5 text-sm text-gray-900 hover:bg-gray-100"
                    onClick={() => focusUnlinkedScene(issue.stripIndex, -192)}
                  >
                    <span className="flex-shrink-0 text-xs text-amber-500">⚠</span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-baseline justify-between gap-2">
                        <span
                          className="truncate text-left group-hover:text-green-600"
                          title={issue.nodeName || issue.typeLabel}
                        >
                          {issue.nodeName || issue.typeLabel}
                        </span>
                        <span className="text-[11px] text-gray-400 flex-shrink-0">
                          {issue.typeLabel}
                        </span>
                      </span>
                      <span className="block text-left text-xs text-gray-500 whitespace-normal break-words">
                        {t("pages.project.edit.graphIssue.unlinkedScene")}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {copyModalOpen && copySceneInfo && currentProject && (
        <CopySceneToProjectModal
          isOpen={copyModalOpen}
          onClose={() => {
            setCopyModalOpen(false);
            setCopySceneInfo(null);
          }}
          sourceContentSceneId={copySceneInfo.contentSceneId}
          sourceProjectId={currentProject.id}
          currentProjectGroupId={currentProjectGroupId}
          onSuccess={() => dispatch(setShouldRerender())}
        />
      )}

      {relinkModalOpen && relinkSceneInfo && currentProject && (
        <RelinkSceneModal
          isOpen={relinkModalOpen}
          onClose={() => {
            setRelinkModalOpen(false);
            setRelinkSceneInfo(null);
            setRelinkInitialSourceSceneId(null);
          }}
          targetContentId={relinkSceneInfo.contentId}
          targetContentSceneId={relinkSceneInfo.contentSceneId}
          targetLabel={relinkSceneInfo.name}
          sceneOptions={sceneOptions}
          projectId={currentProject.id}
          initialSourceContentSceneId={relinkInitialSourceSceneId}
          onSuccess={(undo) => {
            setLastUndo(undo ?? null);
            dispatch(setShouldRerender());
          }}
        />
      )}

      {deleteSceneInfo && (
        <ModalConfirmation
          id={DELETE_SCENE_MODAL_ID}
          componentId={DELETE_SCENE_COMPONENT}
          title={t("pages.project.editNode.deleteSceneConfirmationModal.header")}
          text={t("pages.project.editNode.deleteSceneConfirmationModal.body")}
          submitButtonText={t("general.delete")}
          onClickSubmit={confirmDeleteScene}
          onClickCancel={() => setDeleteSceneInfo(null)}
        />
      )}

      {delinkSceneInfo && (
        <ModalConfirmation
          id={DELINK_MODAL_ID}
          componentId={DELINK_COMPONENT}
          title={t("pages.project.edit.delinkConfirmTitle", {
            name: delinkSceneInfo.name ?? "",
          })}
          text={t("pages.project.edit.delinkConfirmText")}
          submitButtonText={t("pages.project.edit.delinkFromModule")}
          onClickSubmit={confirmDelinkScene}
          onClickCancel={() => setDelinkSceneInfo(null)}
        />
      )}
    </div>
  );
};

export default SVGGraph;
